// This file will export the basic utitlity function to use globally.
import { APPOINTMENT_SLOT_GAP_IN_MINS } from '@constant/appointment';
import cookie from "cookie";
import moment from 'moment';
import { readLocalStorage } from './safeStorage';
import { windowRef } from './window';

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
 *
 * @returns {String}
 */
export function getMobileOperatingSystem() {
  var userAgent = windowRef()?.navigator?.userAgent || windowRef()?.navigator?.vendor || windowRef()?.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone";
  }

  if (/android/i.test(userAgent)) {
    return "Android";
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !windowRef().MSStream) {
    return "IOS";
  }

  return "unknown";
}

export function parseCookies(req) {
  return cookie.parse(req ? req.headers.cookie || "" : document.cookie)
}

export function timeDiffCalc(dateFuture, dateNow, status) {
  let diffInMilliSeconds = Math.abs(dateFuture - dateNow) / 1000;

  // calculate days
  const days = Math.floor(diffInMilliSeconds / 86400);
  diffInMilliSeconds -= days * 86400;

  // calculate hours
  const hours = Math.floor(diffInMilliSeconds / 3600) % 24;
  diffInMilliSeconds -= hours * 3600;

  // calculate minutes
  const minutes = Math.floor(diffInMilliSeconds / 60) % 60;
  diffInMilliSeconds -= minutes * 60;

  let difference: any = '';
  switch (status) {
    case 'Days':
      difference = days;
      break;
    case 'Hours':
      difference = hours;
      break;
    case 'Minutes':
      difference = minutes;
      break;
    case 'Fulldate':
      if (days > 0) {
        difference += (days === 1) ? `${days} day, ` : `${days} days, `;
      }

      difference += (hours === 0 || hours === 1) ? `${hours} hour, ` : `${hours} hours, `;

      difference += (minutes === 0 || hours === 1) ? `${minutes} minutes` : `${minutes} minutes`;
      break;
  }

  return difference;
}

/**
 * This utility function will truncate the long text
 * with the chars provided, for example if chars = 32 then it will
 * truancate the text till 32th character and return the new text.
 * @param {*} desc 
 * @param {*} chars 
 */
export const TruncateText = (desc, chars) => {
  if (desc && desc?.length > chars) {
    return desc.substring(0, chars) + '';
  } else {
    return desc
  }
}
/**
 * Will return time/days spent from now
 * @param {*} date 
 */
export const TimeFromNow = (date) => {
  let timeFromNow = '';
  if (moment(date).fromNow(true) === 'a few seconds') {
    timeFromNow = 'few seconds';
  } else if (moment(date).fromNow(true) === 'a month') {
    timeFromNow = '1 month';
  } else if (moment(date).fromNow(true).includes('minutes')) {
    const timenow = moment(date).fromNow(true);
    timeFromNow = timenow.replace('minutes', 'mins');
  } else if (moment(date).fromNow(true).includes('minute')) {
    const timenow = moment(date).fromNow(true);
    timeFromNow = timenow.replace('minute', 'min');
  } else {
    timeFromNow = moment(date).fromNow(true);
  }
  return timeFromNow;
}

/**
   * Will return the formated date
   * @param {*} date 
   */
export const GetDateValue = (date) => {
  const today = moment().endOf('day');
  const yesterday = moment().subtract(1, 'day').endOf('day');

  if (date <= today && date > yesterday) {
    return 'Today'
  } else if (date < today && date >= yesterday) {
    return 'Yesterday';
  } else {
    // return <DateTime what="date" value={date} />
  }
}

// import { pluralize, underscore } from 'inflected'
let pluralize: any;
let underscore: any;
export function buildRelationshipData(type, ids) {
  let data = []

  if (ids === null || ids?.length === 0) return data

  if (typeof ids === 'string') {
    const obj = { type: underscore(type), id: ids }

    return [obj]
  }

  if (Array.isArray(ids)) {
    data = ids?.map(id => ({
      type: underscore(type),
      id
    }))
  }

  return data
}

export function formatUrlResource(type) {
  if (type === 'main-image') return type

  return pluralize(type)
}

export function createCartIdentifier() {
  return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[x]/g, () =>
    ((Math.random() * 16) | 0).toString(16)
  )
}

export function cartIdentifier(storage) {
  const cartId = createCartIdentifier()

  if (storage.get('mcart') !== null) {
    return storage.get('mcart')
  }

  storage.set('mcart', cartId)

  return cartId
}

export function parseJSON(response) {
  return new Promise(resolve => {
    response.text().then(body => {
      resolve({
        status: response.status,
        ok: response.ok,
        json: body !== '' ? JSON.parse(body) : '{}'
      })
    })
  })
}

function formatFilterString(type, filter) {
  const filterStringArray = Object.keys(filter)?.map(key => {
    const value = filter[key]
    let queryString: any = `${key},${value}`

    if (typeof value === 'object')
      queryString = Object.keys(value)?.map(
        attr => `${key}.${attr},${value[attr]}`
      )

    return `${type}(${queryString})`
  })

  return filterStringArray.join(':')
}

function formatQueryString(key, value) {
  if (key === 'limit' || key === 'offset') {
    return `page${value}`
  }

  if (key === 'filter') {
    const filterValues = Object.keys(value)?.map(filter =>
      formatFilterString(filter, value[filter])
    )

    return `${key}=${filterValues.join(':')}`
  }

  return `${key}=${value}`
}

function buildQueryParams({ includes, sort, limit, offset, filter }) {
  const query: any = {}

  if (includes) {
    query.include = includes
  }

  if (sort) {
    query.sort = `${sort}`
  }

  if (limit) {
    query.limit = `[limit]=${limit}`
  }

  if (offset) {
    query.offset = `[offset]=${offset}`
  }

  if (filter) {
    query.filter = filter
  }

  return Object.keys(query)
    ?.map(k => formatQueryString(k, query[k]))
    .join('&')
}

export function buildURL(endpoint, params) {
  if (
    params.includes ||
    params.sort ||
    params.limit ||
    params.offset ||
    params.filter
  ) {
    const paramsString = buildQueryParams(params)

    return `${endpoint}?${paramsString}`
  }

  return endpoint
}

export function buildRequestBody(body) {
  let parsedBody
  if (body) {
    if (body.options) {
      parsedBody = `{
        "data": ${JSON.stringify(body.data)},
        "options" : ${JSON.stringify(body.options)}
      }`
    } else {
      parsedBody = `{
        "data": ${JSON.stringify(body)}
      }`
    }
  }

  return parsedBody
}

export function buildCartItemData(
  id,
  quantity = null,
  type = 'cart_item',
  flows,
  isSku = false
) {
  const payload = {
    type,
    ...flows
  }

  if (type === 'cart_item') {
    if (isSku)
      Object.assign(payload, {
        sku: id,
        quantity: parseInt(quantity, 10)
      })
    else
      Object.assign(payload, {
        id,
        quantity: parseInt(quantity, 10)
      })
  }

  if (type === 'promotion_item') {
    Object.assign(payload, {
      code: id
    })
  }

  return payload
}

export function buildCartCheckoutData(
  customer,
  billing_address,
  shipping_address
) {
  let parsedCustomer = customer

  if (typeof customer === 'string') parsedCustomer = { id: customer }

  return {
    customer: parsedCustomer,
    billing_address,
    shipping_address
  }
}

export function resetProps(instance) {
  const inst = instance
    ;['includes', 'sort', 'limit', 'offset', 'filter'].forEach(
      e => delete inst[e]
    )
}

export function getCredentials(storage) {
  return JSON.parse(storage.get('moltinCredentials'))
}

export function tokenInvalid(config) {
  const credentials = getCredentials(config.storage)

  return (
    !credentials ||
    !credentials.access_token ||
    credentials.client_id !== config.client_id ||
    Math.floor(Date.now() / 1000) >= credentials.expires
  )
}

export function formatToDateFormat(dateInput, format) {
  if (!Boolean(dateInput)) return "-"
  if (!Boolean(format)) {
    format = "dd-MM-yyyy"
  }
  const date = new Date(dateInput);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const tokens: any = {
    dd: String(day).padStart(2, "0"),
    d: String(day),
    MM: String(month).padStart(2, "0"),
    M: String(month),
    yyyy: String(year),
    yy: String(year).slice(-2),
    MMM: monthNames[month - 1],
    HH: String(hours).padStart(2, "0"),
    H: String(hours),
    mm: String(minutes).padStart(2, "0"),
    m: String(minutes),
    ss: String(seconds).padStart(2, "0"),
    s: String(seconds),
  };

  return format.replace(/dd|d|MMM|MM|M|yyyy|yy|HH|H|mm|m|ss|s/g, (match: any) => tokens[match]);
}

export function formatTimeTo12Hr(time) {
  if (time) {
    let timeNewDateString = time.split(":");
    let timeNewDate = new Date();
    timeNewDate.setHours(parseInt(timeNewDateString[0]));
    timeNewDate.setMinutes(parseInt(timeNewDateString[1]));
    const hour24 = timeNewDate.getHours();
    let minutes: any = (timeNewDate.getMinutes() === 0) ? '00' : timeNewDate.getMinutes();
    minutes = (minutes > 0 && minutes < 10) ? `0${minutes}` : minutes;
    const ampm = (hour24 >= 12) ? 'PM' : 'AM';
    let hour: any = hour24 % 12 || 12;
    //append zero is hour is single digit
    if (hour < 10) {
      hour = `0${hour}`;
    }
    return `${hour}:${minutes} ${ampm}`;
  } else return '';
};


export function getUpcommingDates(number: number = 15, storeConfigData: any = null) {
  var actualDate = new Date();
  var dateArray = [];

  for (var i = 0; i < number; i++) {
    let upcommingDate = new Date(actualDate.getFullYear(), actualDate.getMonth(), actualDate.getDate() + i);
    const month = (upcommingDate.getMonth() + 1).toString().length == 1 ? `0${upcommingDate.getMonth() + 1}` : upcommingDate.getMonth() + 1;
    const date = upcommingDate.getDate().toString().length == 1 ? `0${upcommingDate.getDate()}` : upcommingDate.getDate();
    const currentday = upcommingDate.toLocaleString('en-us', { weekday: 'long' }).substring(0, 3);
    if (!storeConfigData.weeklyOff?.includes(currentday)) {
      const dateObj = {
        displayDate: date,
        displayDay: upcommingDate.toLocaleString('en-us', { weekday: 'long' }).substring(0, 3),
        newDate: upcommingDate,
        dateObj: `${upcommingDate.getFullYear()}-${month}-${date}`,
        active: true
      }
      if (storeConfigData) {
        //validation for weeklyOff
        if (('weeklyOff' in storeConfigData) && storeConfigData.weeklyOff) {
          if (storeConfigData.weeklyOff.toLowerCase() == 'all') dateObj.active = false;
          else if (storeConfigData?.weeklyOff?.includes(dateObj.displayDay)) dateObj.active = false;
        } else dateObj.active = true;
      }
      if (dateObj.active) dateArray.push(dateObj);
    }
  }
  return dateArray;
}

export function timeToMinutes(time: any) {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function minutesToTime(minutes: number) {
  if (minutes === 1440) return '24:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const hh = hours < 10 ? '0' + hours : hours;
  const mm = mins < 10 ? '0' + mins : mins;
  return `${hh}:${mm}`;
}

export function createSlotsWithRange(starttime: any, endtime: any, interval: any = APPOINTMENT_SLOT_GAP_IN_MINS) {
  const startMinutes = timeToMinutes(starttime);
  const endMinutes = timeToMinutes(endtime);

  let effectiveEndMinutes = endMinutes;
  if (effectiveEndMinutes <= startMinutes) {
    effectiveEndMinutes += 1440;
  }

  const timeslots = [];
  for (let current = startMinutes; current <= effectiveEndMinutes; current += interval) {
    timeslots.push(minutesToTime(current % 1440));
  }

  const slotWithRange = timeslots.map((slot: string, index: number) => {
    const nextSlot = timeslots[index + 1] ? timeslots[index + 1] : null;
    return {
      slot,
      range: nextSlot ? `${slot} - ${nextSlot}` : null,
      nextSlot: nextSlot
    };
  });

  return slotWithRange;
}

export function createSlots(start: any, endtime: any, interval: any = APPOINTMENT_SLOT_GAP_IN_MINS, dayStartTime: any = null) {
  let startMinutes = timeToMinutes(start);
  let endMinutes = timeToMinutes(endtime);

  if (Boolean(dayStartTime)) {
    const dayStartMinutes = timeToMinutes(dayStartTime);
    const minutesSinceDayStart = startMinutes - dayStartMinutes;

    let alignedStartMinutes;
    if (minutesSinceDayStart < 0) {
      alignedStartMinutes = dayStartMinutes;
    } else {
      const intervalsPassed = Math.floor(minutesSinceDayStart / interval);
      alignedStartMinutes = dayStartMinutes + (intervalsPassed * interval);
      if (alignedStartMinutes < startMinutes) {
        alignedStartMinutes += interval;
      }
    }
    startMinutes = alignedStartMinutes;
  }

  let effectiveEndMinutes = endMinutes;
  if (effectiveEndMinutes <= startMinutes) {
    effectiveEndMinutes += 1440;
  }

  const timeslots = [];
  for (let current = startMinutes; current <= effectiveEndMinutes; current += interval) {
    timeslots.push(minutesToTime(current % 1440));
  }
  return timeslots;
}

export function createSlotsObjList(starttime: any, endtime: any, interval: any = APPOINTMENT_SLOT_GAP_IN_MINS, blockSlots: any) {
  let currentMinutes = timeToMinutes(starttime);
  let endMinutes = timeToMinutes(endtime);

  let effectiveEndMinutes = endMinutes;
  if (effectiveEndMinutes <= currentMinutes) {
    effectiveEndMinutes += 1440;
  }

  const blockedRanges: { start: number, end: number }[] = [];
  if (Array.isArray(blockSlots)) {
    for (let i = 0; i < blockSlots.length; i++) {
      blockedRanges.push({
        start: timeToMinutes(blockSlots[i].substring(0, 5)),
        end: timeToMinutes(blockSlots[i].substring(6))
      });
    }
  }
  const timeslots = [];
  while (currentMinutes <= effectiveEndMinutes) {
    const normalizedMinutes = currentMinutes % 1440;
    const timeString = minutesToTime(normalizedMinutes);
    const matchingBlock = blockedRanges.find(range => range.start === normalizedMinutes);
    if (matchingBlock) {
      let blockDuration = matchingBlock.end - matchingBlock.start;
      if (blockDuration < 0) {
        blockDuration += 1440;
      }

      const blockAbsoluteEnd = currentMinutes + blockDuration;
      while (currentMinutes < blockAbsoluteEnd) {
        timeslots.push({ slot: minutesToTime(currentMinutes % 1440), active: true, disabled: false });
        currentMinutes += interval;
      }
      if (currentMinutes > effectiveEndMinutes) break;
    }
    else {
      timeslots.push({ slot: timeString, active: true, disabled: false });
      currentMinutes += interval;
    }
  }
  return timeslots;
}


const getImage = (configData, activeGroup, forImg) => {
  if (configData?.genericImages[forImg]) {
    let img = configData?.genericImages[forImg].filter((i: any) => (!i.group || i.group.toLowerCase() === 'both') ? true : i.group.toLowerCase() == activeGroup);
    return img && img[0] && img[0].path;
  } else return '';
}

export function getGenericImages(configData, activeGroup) {
  if (configData?.genericImages) {
    let appBg = getImage(configData, activeGroup, 'appBg');
    let footerBg = getImage(configData, activeGroup, 'footerBg');
    let loginScreenBg = getImage(configData, activeGroup, 'loginScreenBg');
    let hamburgerBg = getImage(configData, activeGroup, 'hamburgerBg');
    return { appBg, footerBg, loginScreenBg, hamburgerBg }
  }
}

export function hex2rgb(colour, alpha) {
  if (colour) {
    var r, g, b;
    if (colour.charAt(0) == "#") {
      colour = colour.substr(1);
    }

    r = colour.charAt(0) + '' + colour.charAt(1);
    g = colour.charAt(2) + '' + colour.charAt(3);
    b = colour.charAt(4) + '' + colour.charAt(5);

    r = parseInt(r, 16);
    g = parseInt(g, 16);
    b = parseInt(b, 16);
    return alpha ? `rgb(${r},${g},${b},${alpha}%)` : `rgb(${r},${g},${b})`;
  } else return ""
}

export function dynamicSort(property: any, order: number) {
  var sortOrder = order;
  if (property == 'lastVisitedOn') {
    return function (a: any, b: any) {
      var result = (new Date(a[property]).getTime() < new Date(b[property]).getTime()) ? -1 : (new Date(a[property]).getTime() > new Date(b[property]).getTime()) ? 1 : 0;
      return result * sortOrder;
    }
  } else {
    return function (a: any, b: any) {
      var result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
      return result * sortOrder;
    }
  }
}

export function updateManifestFile(storeData: any) {
  const theme_color = document.getElementById("theme-color").getAttribute("content");
  const manifestConfig = storeData.configData.storeConfig.manifestConfig;
  const manifestString = JSON.stringify({
    ...{
      "name": `${storeData.tenant}, ${storeData.name}` || 'Respark',
      "short_name": `${storeData.tenant}` || 'Respark',
      "start_url": storeData.url || '/',
      "display": "standalone",
      "background_color": theme_color || "#dee1ec",
      "theme_color": theme_color || "#dee1ec",
      "orientation": "standalone",
      "description": storeData.description,
      "id": storeData.tenantId,
      "icons": [
        {
          "src": manifestConfig.icons['180'],
          "type": "image/png",
          "sizes": "180x180"
        },
        {
          "src": manifestConfig.icons['192'],
          "type": "image/png",
          "sizes": "192x192"
        },
        {
          "src": manifestConfig.icons['384'],
          "type": "image/png",
          "sizes": "384x384"
        },
        {
          "src": manifestConfig.icons['512'],
          "type": "image/png",
          "sizes": "512x512"
        },
        {
          "src": manifestConfig.icons['1024'],
          "type": "image/png",
          "sizes": "1024x1024"
        }
      ]
    },
  });
  const manifestElement = document.getElementById("manifest");
  manifestElement?.setAttribute("href", "data:application/json;charset=utf-8," + encodeURIComponent(manifestString));
}

export function getDateObj(startDate: any = new Date()) {
  let dateObj = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const month = (dateObj.getMonth() + 1).toString().length == 1 ? `0${dateObj.getMonth() + 1}` : dateObj.getMonth() + 1;
  const date = dateObj.getDate().toString().length == 1 ? `0${dateObj.getDate()}` : dateObj.getDate();
  return {
    displayDate: date,
    displayDay: dateObj.toLocaleString('en-us', { weekday: 'long' }).substring(0, 3),
    newDate: dateObj,
    dateObj: `${dateObj.getFullYear()}-${month}-${date}`
  }
}
export function getTofixValue(value: any = 0, isDisplayValue: boolean = false, roundValue: any = 2) {
  const decimalPlaces = readLocalStorage('decimalPlaces');
  const enableRoundOff = readLocalStorage('enableRoundOff') === '1';
  if (decimalPlaces) roundValue = decimalPlaces;
  if (Number(decimalPlaces) != 3) {
    roundValue = enableRoundOff ? 0 : 2;
  }
  if (!value) value = 0;
  value = Number(value).toFixed(roundValue)
  return isDisplayValue ? value : Number(value);
}


export const getCurrencySymbol = () => {
  const currencySymbol = readLocalStorage('currencySymbol');
  return currencySymbol ? currencySymbol : '₹';
}


export const isActiveAtBinaryIndex = (value: any, index: any) => {
  return Boolean((value >> index) % 2)
}

export function addDayToDate(date: any, numberOfDays: number) {
  date = new Date(date);
  const temp = new Date(date);
  return new Date(temp.setDate(temp.getDate() + numberOfDays));
}


export const removeObjRef = (obj) => {
  return Boolean(obj) ? JSON.parse(JSON.stringify(obj)) : null;
}

export const getDisabledDates = (configData) => {
  const disabledDates = [];
  if (Boolean(configData?.weeklyOff)) {
    const weeklyOffDays = configData.weeklyOff.split(",");
    const dayShortNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    for (let i = 0; i < 365; i++) {
      const date = addDayToDate(new Date(), i);
      const day = dayShortNames[date.getDay()];
      if (weeklyOffDays.includes(day)) {
        disabledDates.push(date);
      }
    }
  }
  return disabledDates
}
export function resetDateTimeToNow(date: any = new Date()) {
  const dateToUpdate = new Date(date);
  // const now = new Date();
  // dateToUpdate.setHours(
  //   now.getHours(),
  //   now.getMinutes(),
  //   now.getSeconds(),
  //   now.getMilliseconds()
  // );
  // console.log("dateToUpdate", dateToUpdate)
  // console.log("dateToUpdate.toISOString()", dateToUpdate.toISOString())
  // return dateToUpdate.toISOString();
  const year = dateToUpdate.getFullYear();
  const month = String(dateToUpdate.getMonth() + 1).padStart(2, "0");
  const day = String(dateToUpdate.getDate()).padStart(2, "0");
  const dateString = `${year}-${month}-${day}T00:00:00.000Z`; // "2017-09-10"
  return dateString;
}

export const processTaxList = (taxList, taxebalePrice: any, quantity = 1) => {
  const txchrgs = [];
  taxList.map(taxData => {
    let taxApplied = parseFloat(((parseFloat(taxebalePrice) / 100) * parseFloat(taxData.value)).toFixed(2));

    if (taxData.isInclusive) {
      const totalTaxesApplied = taxList.reduce((a: any, b: any) => { return b.active ? a + Number(b.value) : a; }, 0);
      const itemActualPrice = (taxebalePrice * 100) / (100 + totalTaxesApplied);
      const actualTax = ((itemActualPrice * taxData.value) / 100) * quantity;
      taxApplied = Number(actualTax.toFixed(2));
    }

    const taxObj = {
      id: taxData.id,
      name: taxData.name,
      type: taxData.type,
      taxRate: taxData.value,
      isInclusive: taxData.isInclusive,
      value: parseFloat(taxApplied.toFixed(2)),
    };
    txchrgs.push(taxObj)
  })

  return txchrgs;
};

export const processAppliedTaxes = (filteredTax, taxebalePrice, appliedTaxes) => {
  let txchrgs: any[] = appliedTaxes || [];
  if (Boolean(filteredTax?.length)) {
    filteredTax.map((taxData: any) => {
      let taxApplied = parseFloat(getTofixValue((parseFloat(taxebalePrice) / 100) * parseFloat(taxData.value)));
      if (taxData.isInclusive) {
        let totalTaxesApplied = filteredTax.reduce((a: any, b: any) => { return b.active ? a + Number(b.value) : a; }, 0);
        let itemActualPrice = ((taxebalePrice * 100) / (100 + totalTaxesApplied));
        let actualTax = (itemActualPrice * taxData.value) / 100;
        taxApplied = Number(getTofixValue(actualTax));
      }
      let isAVl = txchrgs.findIndex((at: any) => at.name == taxData.name);
      if (isAVl != -1) {
        txchrgs[isAVl].total = (Number(getTofixValue(appliedTaxes[isAVl].total + taxApplied)));
      } else {
        const taxObj = {
          id: taxData.id,
          name: taxData.name,
          type: taxData.type,
          isInclusive: taxData.isInclusive,
          taxRate: taxData.value,
          total: taxApplied,
          value: parseFloat((taxData.value)?.toFixed(2)),
        }
        txchrgs.push(taxObj);
      }
    })
  }
  return txchrgs
}

export const checkStaffWeekOff = (date, weeklyOff) => {
  if (!date) return false;
  const dayShort = date?.toLocaleString("en-US", { weekday: "long" })?.toLowerCase();
  return Boolean(weeklyOff?.includes(dayShort))
}

export const formattedDuration = (item) => {
  if (!Boolean(item?.duration) || !Boolean(item?.durationType)) return '';
  if (item?.duration && item?.durationType?.toLowerCase() === 'min') {
    if (item.duration >= 60) {
      const hours = Math.floor(item.duration / 60);
      const minutes = item.duration % 60;
      if (minutes === 0) {
        return `${hours} Hr`;
      } else {
        return `${hours} Hr ${minutes} Min`;
      }
    } else {
      return `${item.duration} Min`;
    }
  }
  return `${item?.duration} ${item?.durationType}`;
};

export const getDefaultSlotTime = (configData: any) => {
  return configData && configData?.storeConfig?.appointmentConfig.defaultSlotTime ? configData.storeConfig?.appointmentConfig.defaultSlotTime : 30;
}

export const getServiceDuration = (duration, durationType) => {
  if (durationType?.includes('hrs')) {
    return (parseFloat(duration) * 60).toFixed(1);
  } else return parseFloat(duration);
}

export const timeToMins = (time) => {
  if (!time || typeof time !== 'string' || !time.includes(':')) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export function getBusinessDate(actualDate: Date, dayWiseTiming: any[]) {
  const currentTotalMinutes = (actualDate.getHours() * 60) + actualDate.getMinutes();

  const yesterdayDate = new Date(actualDate);
  yesterdayDate.setDate(actualDate.getDate() - 1);
  const yesterdayTimings = dayWiseTiming[yesterdayDate.getDay()];

  if (yesterdayTimings && yesterdayTimings.startTime && yesterdayTimings.closureTime) {
    const yesterdayStart = timeToMinutes(yesterdayTimings.startTime);
    const yesterdayEnd = timeToMinutes(yesterdayTimings.closureTime);

    const crossesMidnight = yesterdayEnd < yesterdayStart;
    if (crossesMidnight && currentTotalMinutes < yesterdayEnd) {
      const businessDate = new Date(yesterdayDate);
      businessDate.setHours(actualDate.getHours(), actualDate.getMinutes(), actualDate.getSeconds(), actualDate.getMilliseconds());
      return businessDate;
    }
  }

  return actualDate;
}

export function getSystemBusinessDate(dayWiseTiming: any[]): Date {
  const now = new Date();
  return getBusinessDate(now, dayWiseTiming);
}

export const getStoreStartMins = (selectedDate: any, configData: any) => {
  if (!selectedDate || !configData?.dayWiseTiming) return 0;
  const dayOfWeek = new Date(selectedDate).getDay();
  const storeStartTime = configData.dayWiseTiming[dayOfWeek]?.startTime || "00:00";
  return timeToMins(storeStartTime);
};

export const isNextDaySlot = (slotTime: string, storeStartMins: number, firstSlotInMins?: number) => {
  if (firstSlotInMins !== undefined) {
    return timeToMins(slotTime) < firstSlotInMins;
  }
  return timeToMins(slotTime) < storeStartMins;
};

export const getNextDayWarningMessage = (slotsList: any[], storeStartMins: number, selectedDate: any, configData: any, formatToDateFormatFn: any, formatTimeTo12HrFn: any) => {
  if (!slotsList || slotsList.length === 0) return null;
  const firstSlotInMins = timeToMins(slotsList[0].slot);
  const nextDaySlots = slotsList.filter(s => timeToMins(s.slot) < firstSlotInMins);
  if (nextDaySlots.length > 0) {
    const firstNextSlot = formatTimeTo12HrFn(nextDaySlots[0].slot);
    const lastNextSlot = formatTimeTo12HrFn(nextDaySlots[nextDaySlots.length - 1].slot);

    const nextDate = new Date(selectedDate);
    nextDate.setDate(nextDate.getDate() + 1);
    const formattedNextDate = formatToDateFormatFn(nextDate, configData?.dateFormat || "dd-MM-yyyy");

    return `*Note: The available slots from ${firstNextSlot} to ${lastNextSlot} are for the next day (${formattedNextDate}).`;
  }
  return null;
};

export const getEarliestAppointmentTime = (expertAppointments: any) => {
  if (!expertAppointments || expertAppointments.length === 0) return null;
  const timeInMinutesList = expertAppointments.filter((app: any) => app.appointmentTime).map((app: any) => timeToMinutes(app.appointmentTime));
  const earliestMinutes = Math.min(...timeInMinutesList);
  return minutesToTime(earliestMinutes);
}

export const hasPaymentGateway = (gatewayName: string, paymentGatewayList: any[]) => {
    return paymentGatewayList.some((payment) => payment.name?.toLowerCase() === gatewayName.toLowerCase());
};

/**
 * Path segment for /{tenantSlug}-{id}/{storeSlug}/…
 * Strips characters like `&` that break HTTP path parsing (treated as query separators).
 */
export const toPathSlug = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
